/**
 * Edit Manager Modal
 * Allows company administrators to assign/reassign staff members to a manager
 */

import { useEffect, useState } from 'react';
import type { UserProfile } from '../../../../../shared/types';
import { getCompanyUsers, updateManagerStaff } from '../../../services/user.service';
import { BaseModal } from '../../common/BaseModal';
import { ModalButtonGroup } from '../../common/ModalButtonGroup';
import { ErrorMessage } from '../../ErrorMessage';

interface EditManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  manager: UserProfile;
  companyId: string;
  onSuccess: () => void;
}

export function EditManagerModal({ isOpen, onClose, manager, companyId, onSuccess }: EditManagerModalProps) {
  const [allStaff, setAllStaff] = useState<UserProfile[]>([]);
  const [selectedStaffIds, setSelectedStaffIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load all staff members for the company
  useEffect(() => {
    if (isOpen) {
      loadStaff();
      // Initialize selected staff with manager's current assignments
      setSelectedStaffIds(manager.managedStaffIds || []);
    }
  }, [isOpen, manager.managedStaffIds]);


  const loadStaff = async () => {
    setLoading(true);
    setError(null);
    try {
      // Get all active users (both staff and managers)
      const users = await getCompanyUsers(companyId, false);
      // Filter to only staff members
      const staffMembers = users.filter(user => user.userType === 'staff');
      setAllStaff(staffMembers);
    } catch (error) {
      console.error('Error loading staff:', error);
      setError('Failed to load staff members. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStaff = (staffId: string) => {
    setSelectedStaffIds(prev => {
      if (prev.includes(staffId)) {
        return prev.filter(id => id !== staffId);
      } else {
        return [...prev, staffId];
      }
    });
  };

  const handleSelectAll = () => {
    setSelectedStaffIds(allStaff.map(staff => staff.uid));
  };

  const handleDeselectAll = () => {
    setSelectedStaffIds([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      await updateManagerStaff(manager.uid, companyId, selectedStaffIds);
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error updating staff assignments:', error);
      setError(error instanceof Error ? error.message : 'Failed to update staff assignments. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Manage Staff Assignments"
      size="medium"
      closeOnEscape={!submitting}
      closeOnBackdropClick={!submitting}
      loading={loading}
    >

        {/* Manager Info */}
        <div
          style={{
            backgroundColor: '#f3f4f6',
            padding: '16px',
            borderRadius: '8px',
            marginBottom: '24px',
          }}
        >
          <p style={{ fontSize: '14px', color: '#374151', marginBottom: '4px' }}>
            <strong>Manager:</strong> {manager.firstName} {manager.lastName}
          </p>
          <p style={{ fontSize: '14px', color: '#374151' }}>
            <strong>Email:</strong> {manager.email}
          </p>
        </div>

        {error && (
          <div style={{ marginBottom: '16px' }}>
            <ErrorMessage message={error} onDismiss={() => setError(null)} />
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Staff Selection */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                Assign Staff Members
              </label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  type="button"
                  onClick={handleSelectAll}
                  disabled={loading || submitting}
                  style={{
                    padding: '4px 12px',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#3b82f6',
                    backgroundColor: 'transparent',
                    border: '1px solid #3b82f6',
                    borderRadius: '4px',
                    cursor: loading || submitting ? 'not-allowed' : 'pointer',
                  }}
                >
                  Select All
                </button>
                <button
                  type="button"
                  onClick={handleDeselectAll}
                  disabled={loading || submitting}
                  style={{
                    padding: '4px 12px',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6b7280',
                    backgroundColor: 'transparent',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    cursor: loading || submitting ? 'not-allowed' : 'pointer',
                  }}
                >
                  Deselect All
                </button>
              </div>
            </div>

            {loading ? (
              <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                Loading staff members...
              </div>
            ) : allStaff.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                No staff members available to assign.
              </div>
            ) : (
              <div
                style={{
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  maxHeight: '300px',
                  overflow: 'auto',
                }}
              >
                {allStaff.map((staff) => (
                  <label
                    key={staff.uid}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '12px 16px',
                      borderBottom: '1px solid #e5e7eb',
                      cursor: submitting ? 'not-allowed' : 'pointer',
                      backgroundColor: selectedStaffIds.includes(staff.uid) ? '#eff6ff' : 'transparent',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedStaffIds.includes(staff.uid)}
                      onChange={() => handleToggleStaff(staff.uid)}
                      disabled={submitting}
                      style={{
                        marginRight: '12px',
                        width: '18px',
                        height: '18px',
                        cursor: submitting ? 'not-allowed' : 'pointer',
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                        {staff.firstName} {staff.lastName}
                      </div>
                      <div style={{ fontSize: '13px', color: '#6b7280' }}>
                        {staff.email}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}

            <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '8px' }}>
              Selected: {selectedStaffIds.length} staff member{selectedStaffIds.length !== 1 ? 's' : ''}
            </p>
          </div>

          {/* Action Buttons */}
          <ModalButtonGroup
            onCancel={onClose}
            submitType="submit"
            submitLabel="Save Changes"
            isLoading={submitting}
            loadingLabel="Saving..."
            disabled={loading}
          />
        </form>
      </BaseModal>
  );
}
